import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  CampaignOverviewProps,
  GetCampaignResponse,
  GetCampaignProspectsWithRepliesResponse,
  CampaignErrorResponse,
  AgentEvents,
} from '@dreamai/types';
import api from '../../../../api';
import { createProspectCanvasAsset } from './prospectCanvasHelper';

import { useAgent } from '../../../../contexts/AgentContext';
import { generateColorVariants } from '../../sidebar/threadUtils';
import ItemList, { ItemListItem } from '../../../common/ItemList';
import { ReactComponent as FlipBackIcon } from '../../../../icons/flip-back.svg';
import { ReactComponent as StopIcon } from '../../../../icons/stop.svg';
import { ReactComponent as DoubleHeartIcon } from '../../../../icons/double-heart.svg';
import ErrorState from '../../../common/ErrorState';
import DeliverabilityScoreComponent from './DeliverabilityScore';
import { useDependencyUpdates } from '../../../../providers';
import { usePusher } from '../../../../contexts/PusherContext';

import NoRepliesImage from '../../../../icons/no-replies.png';

import './campaign-overview.scss';

interface CampaignOverviewComponentProps extends CampaignOverviewProps {
  threadId?: string;
  updateThreadViewVisualDescription?: (
    visualDescription: string,
  ) => Promise<void>;
  isInboxView?: boolean;
  onInboxViewComponentViewClick?: (componentId: string) => void;
}

const CampaignOverview: React.FC<CampaignOverviewComponentProps> = ({
  campaignId,
  threadId,
  updateThreadViewVisualDescription,
  isInboxView,
  onInboxViewComponentViewClick,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const { selectedAgent } = useAgent();
  const pusherChannel = usePusher();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [campaign, setCampaign] = useState<
    GetCampaignResponse['campaign'] | null
  >(null);
  const [prospectsWithReplies, setProspectsWithReplies] = useState<
    GetCampaignProspectsWithRepliesResponse['prospects']
  >([]);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [repliesError, setRepliesError] = useState(false);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  // Generate color variants from agent's predominant color
  const colorVariants = selectedAgent?.predominantColour
    ? generateColorVariants(selectedAgent.predominantColour)
    : {
        base: '#ECBD2D',
        midDarker: '#D4A91A',
        darker: '#B8950F',
        lighter: '#FDF8E8',
      };

  useEffect(() => {
    const fetchCampaign = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = await getAccessTokenSilently();
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const response = await api.get<
          GetCampaignResponse | CampaignErrorResponse
        >(`/campaigns/${campaignId}`);

        if (response.data.success) {
          setCampaign(response.data.campaign);
        } else {
          setError(response.data.error);
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to fetch campaign',
        );
      } finally {
        setLoading(false);
      }
    };

    if (campaignId) {
      fetchCampaign();
    }
  }, [campaignId, getAccessTokenSilently, refetchTrigger]);

  // Listen for deliverability opt-in changes
  useEffect(() => {
    if (!pusherChannel) return;

    const handleDeliverabilityChange = () => {
      setRefetchTrigger((prev) => prev + 1);
    };

    pusherChannel.bind(
      AgentEvents.DELIVERABILITY_OPT_IN_CHANGED,
      handleDeliverabilityChange,
    );

    return () => {
      pusherChannel.unbind(
        AgentEvents.DELIVERABILITY_OPT_IN_CHANGED,
        handleDeliverabilityChange,
      );
    };
  }, [pusherChannel]);

  // Fetch prospects with replies
  const fetchProspectsWithReplies = useCallback(async () => {
    if (!campaignId) return;

    try {
      setLoadingReplies(true);
      setRepliesError(false);
      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await api.get<
        GetCampaignProspectsWithRepliesResponse | CampaignErrorResponse
      >(`/campaigns/${campaignId}/prospects-with-replies`);

      if (response.data.success) {
        setProspectsWithReplies(response.data.prospects);
      }
    } catch (err) {
      console.error('Failed to fetch prospects with replies:', err);
      setRepliesError(true);
    } finally {
      setLoadingReplies(false);
    }
  }, [campaignId, getAccessTokenSilently]);

  useEffect(() => {
    fetchProspectsWithReplies();
  }, [fetchProspectsWithReplies]);

  // Listen for campaign updates (prospect added/removed)
  const { registerCallback } = useDependencyUpdates();
  useEffect(() => {
    const unregister = registerCallback('campaigns', campaignId, (update) => {
      console.log('Campaign updated in overview, reloading...');
      // Reload campaign data and prospects with replies
      const fetchCampaign = async () => {
        try {
          const token = await getAccessTokenSilently();
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          const response = await api.get<
            GetCampaignResponse | CampaignErrorResponse
          >(`/campaigns/${campaignId}`);
          if (response.data.success) {
            setCampaign(response.data.campaign);
          }
        } catch (err) {
          console.error('Failed to refresh campaign:', err);
        }
      };
      fetchCampaign();
      fetchProspectsWithReplies();
    });

    return () => unregister();
  }, [
    campaignId,
    registerCallback,
    getAccessTokenSilently,
    fetchProspectsWithReplies,
  ]);

  // Listen for scheduled email updates (sent emails)
  useEffect(() => {
    const unregister = registerCallback(
      'scheduledEmail',
      campaignId,
      (update) => {
        console.log('Scheduled email updated, reloading replies...');
        // Reload prospects with replies to update awaitingOurResponse status
        fetchProspectsWithReplies();
      },
    );

    return () => unregister();
  }, [campaignId, registerCallback, fetchProspectsWithReplies]);

  // Handle marking prospect as viewed (dismiss unread pill)
  const handleMarkProspectViewed = useCallback(
    async (conversationId: number, prospectId: number) => {
      // Optimistic update: immediately update the UI
      setProspectsWithReplies((prevProspects) =>
        prevProspects.map((p) =>
          p.conversationId === conversationId
            ? { ...p, hasUnreadReplies: false }
            : p,
        ),
      );

      try {
        const token = await getAccessTokenSilently();
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        await api.post(
          `/campaigns/conversations/${conversationId}/mark-viewed`,
          {
            prospectId,
          },
        );

        // Reload prospects to ensure consistency with backend
        fetchProspectsWithReplies();
      } catch (error) {
        console.error('Error marking prospect as viewed:', error);
        // Revert optimistic update on error
        fetchProspectsWithReplies();
      }
    },
    [getAccessTokenSilently, fetchProspectsWithReplies],
  );

  // Handle marking conversation as handled (dismiss awaiting reply pill)
  const handleMarkConversationHandled = useCallback(
    async (conversationId: number) => {
      // Optimistic update: immediately update the UI
      setProspectsWithReplies((prevProspects) =>
        prevProspects.map((p) =>
          p.conversationId === conversationId
            ? { ...p, awaitingOurResponse: false, mayWantToRespond: false }
            : p,
        ),
      );

      try {
        const token = await getAccessTokenSilently();
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        await api.post(
          `/campaigns/conversations/${conversationId}/mark-handled`,
        );

        // Reload prospects to ensure consistency with backend
        fetchProspectsWithReplies();
      } catch (error) {
        console.error('Error marking conversation as handled:', error);
        // Revert optimistic update on error
        fetchProspectsWithReplies();
      }
    },
    [getAccessTokenSilently, fetchProspectsWithReplies],
  );

  // Convert prospects with replies to ItemList format
  const convertProspectsToItems = (): ItemListItem[] => {
    return prospectsWithReplies.map((prospect) => ({
      id: prospect.id.toString(),
      icon: <FlipBackIcon />, // Reply icon
      img: prospect?.orgImageUrl || undefined, // Company avatar
      imagePlaceholder: prospect.orgName
        ? prospect.orgName.charAt(0).toUpperCase()
        : undefined, // First letter of org name
      title: prospect.name || 'Prospect',
      description: prospect.orgName, // Company name
      subDescription: prospect.jobTitle || undefined, // Job title
      date: prospect.lastReplyAt,
      pill: prospect.hasUnreadReplies
        ? {
            text: '🎉 New reply',
            variant: 'magic' as const,
            onCancel: () =>
              handleMarkProspectViewed(prospect.conversationId, prospect.id),
          }
        : prospect.awaitingOurResponse
          ? {
              text: 'Awaiting reply',
              variant: 'red' as const,
              onCancel: () =>
                handleMarkConversationHandled(prospect.conversationId),
            }
          : prospect.mayWantToRespond
            ? {
                text: 'May want to respond',
                variant: 'beige' as const,
                onCancel: () =>
                  handleMarkConversationHandled(prospect.conversationId),
              }
            : undefined,
    }));
  };

  const handleProspectClick = useCallback(
    async (prospectId: string) => {
      if (!threadId) {
        console.error('No threadId available for creating canvas asset');
        return;
      }

      // Find the prospect to get its name and conversation ID
      const prospect = prospectsWithReplies.find(
        (p) => p.id.toString() === prospectId,
      );
      const prospectName = prospect?.name || 'Prospect';

      // Automatically mark the prospect as viewed when opening it
      if (prospect) {
        handleMarkProspectViewed(prospect.conversationId, prospect.id);
      }

      await createProspectCanvasAsset({
        prospectId,
        prospectName,
        threadId,
        getAccessTokenSilently,
      });
    },
    [
      threadId,
      getAccessTokenSilently,
      prospectsWithReplies,
      handleMarkProspectViewed,
    ],
  );

  if (loading || (!campaign && !error)) {
    return (
      <div className='campaign-overview-loading'>
        <div className='campaign-top-bit'>
          <div className='pills'>
            <div className='pill-left'></div>
            <div className='pill-right'></div>
          </div>
          <div className='campaign-mid-bit'></div>
        </div>

        <div
          className='campaign-colour-bit'
          style={{
            border: `1px solid ${colorVariants.base}`,
            background: `linear-gradient(90deg, ${colorVariants.lighter} 0%, ${colorVariants.base} 100%)`,
          }}
        ></div>

        <div className='campaign-lower-bit'></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='campaign-overview-loading'>
        <ErrorState
          title="I can't load your campaign details right now"
          subtitle='You can try again, or refresh the page.'
        />
      </div>
    );
  }

  if (!campaign) {
    // Shouldn't get here
    return <div></div>;
  }

  return (
    <div className='campaign-overview'>
      <div className='campaign-status-topper'>
        <h2>{campaign.name}</h2>
        <div className='campaign-status-row'>
          <div
            className={`status-pill ${campaign.archived ? 'status-pill-red' : campaign.autopilot ? 'status-pill-green' : ''}`}
          >
            {campaign.archived
              ? 'Campaign archived'
              : campaign.autopilot
                ? 'Campaign live'
                : 'Campaign paused'}
          </div>
          <span>
            {campaign.dash_data.emails_sent_last_24_hours} messages sent in the
            last 24 hours
          </span>
        </div>
      </div>

      <div
        className='campaign-details-area'
        style={{
          border: `1px solid ${colorVariants.base}`,
          background: `linear-gradient(90deg, ${colorVariants.lighter} 0%, ${colorVariants.base} 100%)`,
        }}
      >
        <div className='pro-image'>
          {selectedAgent?.dreamieImageUrl ? (
            <img src={selectedAgent.dreamieImageUrl} alt='Agent' />
          ) : (
            <div
              className='agent-avatar-fallback'
              style={{
                backgroundColor: colorVariants.midDarker,
              }}
            >
              ?
            </div>
          )}
        </div>
        <div className='information-area'>
          <b>Campaign goal</b>
          <p>{campaign.useCaseDescription}</p>
        </div>
      </div>

      <div className='stats-line'>
        <div className='stat'>
          <span className='stat-label'>Emails sent</span>
          <span className='stat-value'>
            {campaign.dash_data.messages_sent || '-'}
          </span>
        </div>
        <div className='stat'>
          <span className='stat-label'>Replies</span>
          <span className='stat-value'>
            {campaign.dash_data.replies || '-'}
          </span>
        </div>
        <div className='stat stat-deliverability'>
          <span className='stat-label'>Deliverability</span>
          <DeliverabilityScoreComponent
            deliverability={campaign.dash_data.deliverability}
            threadId={threadId}
            onOptInSuccess={() => setRefetchTrigger((prev) => prev + 1)}
          />
        </div>
        <div className='stat'>
          <span className='stat-label'>Open rate</span>
          <span className='stat-value'>
            {campaign.dash_data.open_rate
              ? `${campaign.dash_data.open_rate}%`
              : '-'}
          </span>
        </div>
        <div className='stat'>
          <span className='stat-label'>Bounce rate</span>
          <span className='stat-value'>
            {campaign.dash_data.bounce_rate
              ? `${campaign.dash_data.bounce_rate}%`
              : '-'}
          </span>
        </div>
        {campaign.dash_data.link_click_rate &&
        parseFloat(campaign.dash_data.link_click_rate) > 0 ? (
          <div className='stat'>
            <span className='stat-label'>Click rate:</span>
            <span className='stat-value'>
              {campaign.dash_data.link_click_rate}%
            </span>
          </div>
        ) : (
          <div className='stat'>
            <span className='stat-label'>Prospect count</span>
            <span className='stat-value'>{campaign.prospectCount || '-'}</span>
          </div>
        )}
      </div>

      {/* Prospects with Replies Section */}
      <ItemList
        heading='Replies'
        items={convertProspectsToItems()}
        emptyHeader={`You've not got any replies yet`}
        emptyText={`When a prospect replies to your message you’ll be able to view them here.`}
        emptyImage={NoRepliesImage}
        loading={loadingReplies}
        error={repliesError}
        onItemClick={handleProspectClick}
        banner={
          prospectsWithReplies.filter((p) => p.hasUnreadReplies).length > 0
            ? {
                icon: (
                  <div className='double-heart-icon-banner'>
                    <DoubleHeartIcon />
                  </div>
                ),
                text: `Great news! You've got ${prospectsWithReplies.filter((p) => p.hasUnreadReplies).length} new ${prospectsWithReplies.filter((p) => p.hasUnreadReplies).length === 1 ? 'reply' : 'replies'}`,
              }
            : prospectsWithReplies.filter((p) => p.awaitingOurResponse).length >
                0
              ? {
                  icon: (
                    <div className='stop-icon-banner'>
                      <StopIcon />
                    </div>
                  ),
                  text: `${prospectsWithReplies.filter((p) => p.awaitingOurResponse).length} ${prospectsWithReplies.filter((p) => p.awaitingOurResponse).length === 1 ? 'prospect is' : 'prospects are'} waiting for a reply`,
                }
              : undefined
        }
      />
    </div>
  );
};

export default CampaignOverview;
